/**
 * API-фасад конструктора отчётов (backend /api/v1/reports).
 *
 * Meta-справочники берутся ТОЛЬКО из backend (KLS) — без хардкод-списков на фронте.
 */

import { del, downloadFile, get, post, postFormData, put } from "@/lib/api-client-v2";

// ── Типы (контракт Pydantic-схем бэкенда) ──────────────────────────────
export type ReportTemplateStatus = "draft" | "published" | "archived";
export type ReportStatus = "pending" | "processing" | "ready" | "failed";

export interface QualifierOption {
  code: string;
  label: string;
}

export interface DataSourceMeta {
  code: string;
  label: string;
  kind: string;
}

export interface ReportMeta {
  report_types: QualifierOption[];
  data_sources: DataSourceMeta[];
  param_types: QualifierOption[];
  engines: QualifierOption[];
}

export interface ReportCategory {
  id: number;
  code: string;
  name: string;
  parent_id: number | null;
  is_active: boolean;
}

export interface ReportTemplateParam {
  id?: number;
  code: string;
  label: string;
  param_type: string;
  is_required: boolean;
  default_value?: unknown;
  ui_hint?: string | null;
  display_order: number;
}

export interface ReportTemplate {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  engine: string;
  category_id?: number | null;
  organization_id?: number | null;
  is_active: boolean;
  is_default: boolean;
  context_meta: Record<string, unknown>;
  status: ReportTemplateStatus;
  version: number;
  template_file_minio?: string | null;
  template_file_meta?: {
    name?: string;
    mime?: string;
    size?: number;
    sha256?: string;
  };
  created_by?: string | null;
  updated_by?: string | null;
  published_at?: string | null;
  archived_at?: string | null;
  created_at: string;
  updated_at?: string | null;
  params: ReportTemplateParam[];
}

export interface ReportTemplateListItem {
  id: string;
  code: string;
  name: string;
  engine: string;
  status: ReportTemplateStatus;
  is_active: boolean;
  is_default: boolean;
  category_id?: number | null;
  organization_id?: number | null;
  version: number;
  updated_at?: string | null;
}

export interface ReportTemplateListResponse {
  items: ReportTemplateListItem[];
  total: number;
  skip: number;
  limit: number;
}

export interface ReportTemplatePublishLog {
  id: number;
  template_id: string;
  from_status?: string | null;
  to_status: string;
  reason?: string | null;
  changed_by?: string | null;
  changed_at: string;
}

export interface ReportInstance {
  id: string;
  template_id: string;
  params: Record<string, unknown>;
  status: ReportStatus;
  result_file_minio?: string | null;
  file_name?: string | null;
  file_mime?: string | null;
  file_size?: number | null;
  created_by?: string | null;
  generated_at?: string | null;
  error_message?: string | null;
  created_at: string;
}

export interface ReportListResponse {
  items: ReportInstance[];
  total: number;
  skip: number;
  limit: number;
}

export interface CloneRequest {
  code: string;
  name?: string;
}

export interface RestoreRequest {
  reason?: string;
  replace_default?: boolean;
  restore_as_non_default?: boolean;
}

/** Сохранить Blob как файл в браузере (proxy-скачивание через API). */
export function saveBlobAsFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Запросы ─────────────────────────────────────────────────────────────
export async function fetchReportMeta(): Promise<ReportMeta> {
  return get<ReportMeta>("/reports/metadata/qualifiers");
}

export async function fetchReportCategories(): Promise<ReportCategory[]> {
  return get<ReportCategory[]>("/reports/categories");
}

export async function listReportTemplates(params: {
  status?: ReportTemplateStatus;
  engine?: string;
  category_id?: number;
  search?: string;
  skip?: number;
  limit?: number;
}): Promise<ReportTemplateListResponse> {
  const query: Record<string, string | number | boolean> = {
    skip: params.skip ?? 0,
    limit: params.limit ?? 50,
  };
  if (params.status) query.status = params.status;
  if (params.engine) query.engine = params.engine;
  if (params.category_id !== undefined && params.category_id !== null) query.category_id = params.category_id;
  if (params.search) query.search = params.search;
  return get<ReportTemplateListResponse>("/reports/templates", query);
}

export async function getReportTemplate(id: string): Promise<ReportTemplate> {
  return get<ReportTemplate>(`/reports/templates/${id}`);
}

export async function createReportTemplate(data: {
  code: string;
  name: string;
  description?: string;
  engine: string;
  category_id?: number | null;
  organization_id?: number | null;
  is_active?: boolean;
  is_default?: boolean;
  context_meta?: Record<string, unknown>;
  params?: Array<Omit<ReportTemplateParam, "id">>;
}): Promise<ReportTemplate> {
  return post<ReportTemplate>("/reports/templates", data);
}

export async function updateReportTemplate(
  id: string,
  data: {
    name?: string;
    description?: string;
    engine?: string;
    category_id?: number | null;
    organization_id?: number | null;
    is_active?: boolean;
    is_default?: boolean;
    context_meta?: Record<string, unknown>;
    params?: Array<Omit<ReportTemplateParam, "id">>;
  },
): Promise<ReportTemplate> {
  return put<ReportTemplate>(`/reports/templates/${id}`, data);
}

export async function deleteReportTemplate(id: string): Promise<void> {
  return del(`/reports/templates/${id}`);
}

export async function publishReportTemplate(id: string, reason?: string): Promise<ReportTemplate> {
  return post<ReportTemplate>(`/reports/templates/${id}/publish`, { reason });
}

export async function archiveReportTemplate(id: string, reason?: string): Promise<ReportTemplate> {
  return post<ReportTemplate>(`/reports/templates/${id}/archive`, { reason });
}

export async function restoreReportTemplate(
  id: string,
  data: RestoreRequest = {},
): Promise<ReportTemplate> {
  return post<ReportTemplate>(`/reports/templates/${id}/restore`, data);
}

export async function cloneReportTemplate(id: string, data: CloneRequest): Promise<ReportTemplate> {
  return post<ReportTemplate>(`/reports/templates/${id}/clone`, data);
}

export async function fetchReportTemplatePublishLog(id: string): Promise<ReportTemplatePublishLog[]> {
  return get<ReportTemplatePublishLog[]>(`/reports/templates/${id}/publish-log`);
}

export async function uploadReportTemplateFile(id: string, file: File): Promise<ReportTemplate> {
  const formData = new FormData();
  formData.append("file", file);
  return postFormData<ReportTemplate>(`/reports/templates/${id}/file`, formData);
}

export async function downloadReportTemplateFile(id: string): Promise<Blob> {
  return downloadFile(`/reports/templates/${id}/file`);
}

/** Образец .docx под пресет (objects + docxtpl). */
export async function downloadReportSampleFile(params?: {
  engine?: string;
  preset?: string;
}): Promise<Blob> {
  return downloadFile("/reports/templates/sample-file", {
    engine: params?.engine ?? "docxtpl",
    preset: params?.preset ?? "objects",
  });
}

export async function listReports(params: {
  template_id?: string;
  status?: ReportStatus;
  skip?: number;
  limit?: number;
}): Promise<ReportListResponse> {
  const query: Record<string, string | number | boolean> = {
    skip: params.skip ?? 0,
    limit: params.limit ?? 50,
  };
  if (params.template_id) query.template_id = params.template_id;
  if (params.status) query.status = params.status;
  return get<ReportListResponse>("/reports/reports", query);
}

export async function createReport(data: {
  template_id: string;
  params?: Record<string, unknown>;
}): Promise<ReportInstance> {
  return post<ReportInstance>("/reports/reports", data);
}

export async function generateReport(id: string): Promise<ReportInstance> {
  return post<ReportInstance>(`/reports/reports/${id}/generate`);
}

export async function getReport(id: string): Promise<ReportInstance> {
  return get<ReportInstance>(`/reports/reports/${id}`);
}

/** Скачивание результата через backend (без MinIO hostname в браузере). */
export async function downloadReportResult(id: string): Promise<Blob> {
  return downloadFile(`/reports/reports/${id}/download`);
}